import type { RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useEtCoreAuthStore } from "@/stores/etCoreAuth";
import { baseAxiosInstance } from "@/api/base";
import Config from "@/common/config";
import { confirmRegisteredLeaveGuards } from "@/composables/leaveGuardRegistry";

const Login = () => import("@/views/Login.vue");
const Registration = () => import("@/views/Registration.vue");
const RegistrationSuccess = () => import("@/views/RegistrationSuccess.vue");
const RegistrationActivation = () =>
  import("@/views/RegistrationActivation.vue");
const RegistrationEmailConfirm = () =>
  import("@/views/RegistrationEmailConfirm.vue");
const EmailConfirmUpdate = () => import("@/views/EmailConfirmUpdate.vue");
const NoMobile = () => import("@/views/NoMobile.vue");
const Dashboard = () => import("@/views/Dashboard.vue");
const TruckDispositionMap = () => import("@/views/TruckDispositionMap.vue");
const AddressesMap = () => import("@/views/AddressesMap.vue");
const UserManagement = () => import("@/views/UserManagement.vue");
const UserProfile = () => import("@/views/UserProfile.vue");
const LoginInfoEdit = () => import("@/views/LoginInfoEdit.vue");
const InTransitReport = () => import("@/views/InTransitReport.vue");
const OpenOrderReport = () => import("@/views/OpenOrderReport.vue");
const WarehouseInventoryReport = () =>
  import("@/views/WarehouseInventoryReport.vue");
const ChargeWeightCalculator = () =>
  import("@/views/ChargeWeightCalculator.vue");
const CreateOffer = () => import("@/views/CreateOffer.vue");
const OfferSent = () => import("@/views/OfferSent.vue");
const InventoryManagement = () => import("@/views/InventoryManagement.vue");
const TermsOfUse = () => import("@/views/TermsOfUse.vue");
const TermsOfUseAccept = () => import("@/views/TermsOfUseAccept.vue");
const PrivacyPolicy = () => import("@/views/PrivacyPolicy.vue");
const PrivacyPolicyAccept = () => import("@/views/PrivacyPolicyAccept.vue");
const PasswordForgot = () => import("@/views/PasswordForgot.vue");
const PasswordForgotSuccess = () => import("@/views/PasswordForgotSuccess.vue");
const PasswordReset = () => import("@/views/PasswordReset.vue");
const PasswordChangeRequired = () =>
  import("@/views/PasswordChangeRequired.vue");
const PasswordChangeOptional = () =>
  import("@/views/PasswordChangeOptional.vue");
const PageNotFound = () => import("@/views/PageNotFound.vue");
const Download = () => import("@/views/Download.vue");
const CustomerAddress = () => import("@/views/CustomerAddress.vue");
const CustomerAddressRequest = () =>
  import("@/views/CustomerAddressRequest.vue");
const CustomerAddressConfirmation = () =>
  import("@/views/CustomerAddressConfirmation.vue");
const PermissionUsageQuery = () => import("@/views/PermissionUsageQuery.vue");
const Translation = () => import("@/views/TranslationTool.vue");
const Address = () => import("@/views/Address.vue");
const AddressChange = () => import("@/views/AddressChange.vue");
const RequestSent = () => import("@/views/RequestSent.vue");
const RequestConfirmed = () => import("@/views/RequestConfirmed.vue");
const QuoteTool = () => import("@/views/QuoteTool.vue");
const QuoteToolPrint = () => import("@/views/QuoteToolPrint.vue");
const ShipmentTracking = () => import("@/views/ShipmentTracking.vue");

export enum RouteNames {
  PAGE_NOT_FOUND = "404",
  DOWNLOAD = "Download",
  NO_MOBILE = "NoMobile",
  LOGIN = "Login",
  REGISTRATION = "Registration",
  REGISTRATION_SUCCESS = "RegistrationSuccess",
  REGISTRATION_ACTIVATION = "RegistrationActivation",
  REGISTRATION_EMAIL_CONFIRM = "RegistrationEmailConfirm",
  EMAIL_CONFIRM_UPDATE = "EmailConfirmUpdate",
  DASHBOARD = "Dashboard",
  TRUCK_DISPOSITION_MAP = "TruckDispositionMap",
  ADDRESSES_MAP = "AddressesMap",
  USER_MANAGEMENT = "UserManagement",
  USER_PROFILE = "UserProfile",
  LOGIN_INFO_EDIT = "LoginInfoEdit",
  REPORT_ITR = "InTransitReport",
  REPORT_OOR = "OpenOrderReport",
  REPORT_WIR = "WarehouseInventoryReport",
  CHARGE_WEIGHT_CALCULATOR = "ChargeWeightCalculator",
  NEW_OFFER = "NewOffer",
  OFFER_SENT = "OfferSent",
  INVENTORY_MANAGEMENT = "InventoryManagement",
  INVENTORY_MANAGEMENT_INCOMING = "InventoryManagementIncoming",
  INVENTORY_MANAGEMENT_OUTGOING = "InventoryManagementOutgoing",
  INVENTORY_MANAGEMENT_RECEIVED = "InventoryManagementReceived",
  INVENTORY_MANAGEMENT_SENT = "InventoryManagementSent",
  INVENTORY_MANAGEMENT_INVENTORY = "InventoryManagementInventory",
  TERMS_OF_USE = "TermsOfUse",
  TERMS_OF_USE_ACCEPT = "TermsOfUseAccept",
  PRIVACY_POLICY = "PrivacyPolicy",
  PRIVACY_POLICY_ACCEPT = "PrivacyPolicyAccept",
  PASSWORD_FORGOT = "PasswordForgot",
  PASSWORD_FORGOT_SUCCESS = "PasswordForgotSuccess",
  PASSWORD_RESET = "PasswordReset",
  PASSWORD_CHANGE_REQUIRED = "PasswordChangeRequired",
  PASSWORD_CHANGE_OPTIONAL = "PasswordChangeOptional",
  CUSTOMER_ADDRESS = "CustomerAddress",
  CUSTOMER_ADDRESS_REQUEST = "CustomerAddressRequest",
  CUSTOMER_ADDRESS_CONFIRMATION = "CustomerAddressConfirmation",
  PERMISSION_USAGE_QUERY = "PermissionUsageQuery",
  TRANSLATION_TOOL = "TranslationTool",
  ADDRESS = "Address",
  ADDRESS_CHANGE = "AddressChange",
  REQUEST_SENT = "RequestSent",
  REQUEST_CONFIRMED = "RequestConfirmed",
  QUOTE_TOOL = "QuoteTool",
  QUOTE_TOOL_PRINT = "QuoteToolPrint",
  SHIPMENT_TRACKING = "ShipmentTracking",
}

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: { name: RouteNames.LOGIN },
  },
  {
    path: "/login",
    name: RouteNames.LOGIN,
    component: Login,
  },
  {
    path: "/registration",
    name: RouteNames.REGISTRATION,
    component: Registration,
  },
  {
    path: "/registration/success",
    name: RouteNames.REGISTRATION_SUCCESS,
    component: RegistrationSuccess,
  },
  {
    path: "/registration/activation",
    name: RouteNames.REGISTRATION_ACTIVATION,
    component: RegistrationActivation,
    props: (route) => ({
      token: route.query.token,
    }),
  },
  {
    path: "/registration/email-confirm",
    name: RouteNames.REGISTRATION_EMAIL_CONFIRM,
    component: RegistrationEmailConfirm,
  },
  {
    path: "/email-confirm-update",
    name: RouteNames.EMAIL_CONFIRM_UPDATE,
    component: EmailConfirmUpdate,
    props: (route) => ({
      token: route.query.token,
      check: route.query.check,
    }),
  },
  {
    path: "/dashboard",
    name: RouteNames.DASHBOARD,
    component: Dashboard,
  },
  {
    path: "/user-management",
    name: RouteNames.USER_MANAGEMENT,
    component: UserManagement,
  },
  {
    path: "/user/:id",
    name: RouteNames.USER_PROFILE,
    component: UserProfile,
    meta: {
      trackAsUrl: "/user",
    },
  },
  {
    path: "/logininfo",
    name: RouteNames.LOGIN_INFO_EDIT,
    component: LoginInfoEdit,
  },
  {
    path: "/report/itr",
    name: RouteNames.REPORT_ITR,
    component: InTransitReport,
  },
  {
    path: "/report/oor",
    name: RouteNames.REPORT_OOR,
    component: OpenOrderReport,
  },
  {
    path: "/report/wir",
    name: RouteNames.REPORT_WIR,
    component: WarehouseInventoryReport,
  },
  {
    path: "/chargeWeightCalculator",
    name: RouteNames.CHARGE_WEIGHT_CALCULATOR,
    component: ChargeWeightCalculator,
  },
  {
    path: "/ims/:inventoryManagementPage",
    name: RouteNames.INVENTORY_MANAGEMENT,
    component: InventoryManagement,
    props: true, // pass the page name to the component
  },
  {
    path: "/ims/deliveryIncoming",
    name: RouteNames.INVENTORY_MANAGEMENT_INCOMING,
    component: InventoryManagement,
    props: () => ({ inventoryManagementPage: "deliveryIncoming" }),
  },
  {
    path: "/ims/deliveryOutgoing",
    name: RouteNames.INVENTORY_MANAGEMENT_OUTGOING,
    component: InventoryManagement,
    props: () => ({ inventoryManagementPage: "deliveryOutgoing" }),
  },
  {
    path: "/ims/deliveryReceived",
    name: RouteNames.INVENTORY_MANAGEMENT_RECEIVED,
    component: InventoryManagement,
    props: () => ({ inventoryManagementPage: "deliveryReceived" }),
  },
  {
    path: "/ims/deliverySent",
    name: RouteNames.INVENTORY_MANAGEMENT_SENT,
    component: InventoryManagement,
    props: () => ({ inventoryManagementPage: "deliverySent" }),
  },
  {
    path: "/ims/inventoryIms",
    name: RouteNames.INVENTORY_MANAGEMENT_INVENTORY,
    component: InventoryManagement,
    props: () => ({ inventoryManagementPage: "inventoryIms" }),
  },
  {
    path: "/terms-of-service",
    name: RouteNames.TERMS_OF_USE,
    component: TermsOfUse,
  },
  {
    path: "/terms-of-service-accept",
    name: RouteNames.TERMS_OF_USE_ACCEPT,
    component: TermsOfUseAccept,
    props: (route) => ({ token: route.query.token }),
  },
  {
    path: "/privacy-policy",
    name: RouteNames.PRIVACY_POLICY,
    component: PrivacyPolicy,
  },
  {
    path: "/privacy-policy-accept",
    name: RouteNames.PRIVACY_POLICY_ACCEPT,
    component: PrivacyPolicyAccept,
    props: (route) => ({ token: route.query.token }),
  },
  {
    path: "/password-forgot",
    name: RouteNames.PASSWORD_FORGOT,
    component: PasswordForgot,
  },
  {
    path: "/password-forgot/success",
    name: RouteNames.PASSWORD_FORGOT_SUCCESS,
    component: PasswordForgotSuccess,
  },
  {
    path: "/password-reset",
    name: RouteNames.PASSWORD_RESET,
    component: PasswordReset,
    props: (route) => ({
      token: route.query.token,
    }),
  },
  {
    path: "/password-change-required",
    name: RouteNames.PASSWORD_CHANGE_REQUIRED,
    component: PasswordChangeRequired,
    props: (route) => ({
      token: route.query.token,
    }),
  },
  {
    path: "/password-change-optional",
    name: RouteNames.PASSWORD_CHANGE_OPTIONAL,
    component: PasswordChangeOptional,
    props: () => ({
      token: baseAxiosInstance.defaults.headers.common["Authorization"],
    }),
  },
  {
    path: "/download",
    name: RouteNames.DOWNLOAD,
    component: Download,
    props: (route) => ({
      token: route.query.token,
      fileName: route.query.fileName,
    }),
  },
  {
    path: "/:pathMatch(.*)*",
    name: RouteNames.PAGE_NOT_FOUND,
    component: PageNotFound,
  },
  {
    path: "/no-mobile",
    name: RouteNames.NO_MOBILE,
    component: NoMobile,
  },
];
// Add routes that should only be reachable in ET Compass
if (Config.isCompass) {
  routes.push({
    path: "/address",
    name: RouteNames.ADDRESS,
    component: Address,
    meta: { requiresEtCoreAuth: true },
  });
  routes.push({
    path: "/address-change/change-logs/:changeLogId",
    name: RouteNames.ADDRESS_CHANGE,
    component: AddressChange,
    meta: { requiresEtCoreAuth: true },
  });
  routes.push({
    path: "/quote-tool/:quoteNumber?",
    name: RouteNames.QUOTE_TOOL,
    component: QuoteTool,
    props: (route) => ({
      quoteNumber: route.params.quoteNumber,
    }),
  });
  routes.push({
    path: "/quote-tool/print/:quoteId",
    name: RouteNames.QUOTE_TOOL_PRINT,
    component: QuoteToolPrint,
  });
  routes.push({
    path: "/request-sent",
    name: RouteNames.REQUEST_SENT,
    component: RequestSent,
  });
  routes.push({
    path: "/request-confirmed",
    name: RouteNames.REQUEST_CONFIRMED,
    component: RequestConfirmed,
  });
  routes.push({
    path: "/debtor-creditor",
    name: RouteNames.CUSTOMER_ADDRESS,
    component: CustomerAddress,
    meta: { requiresEtCoreAuth: true },
  });
  routes.push({
    path: "/debtor-creditor-requested",
    name: RouteNames.CUSTOMER_ADDRESS_REQUEST,
    component: CustomerAddressRequest,
  });
  routes.push({
    path: "/debtor-creditor-confirmed",
    name: RouteNames.CUSTOMER_ADDRESS_CONFIRMATION,
    component: CustomerAddressConfirmation,
  });
  routes.push({
    path: "/truck-disposition-map",
    name: RouteNames.TRUCK_DISPOSITION_MAP,
    component: TruckDispositionMap,
  });
  routes.push({
    path: "/addresses-map",
    name: RouteNames.ADDRESSES_MAP,
    component: AddressesMap,
  });
  routes.push({
    path: "/permission-usage-query",
    name: RouteNames.PERMISSION_USAGE_QUERY,
    component: PermissionUsageQuery,
  });
  routes.push({
    path: "/translationTool",
    name: RouteNames.TRANSLATION_TOOL,
    component: Translation,
  });
}
// Add routes that should only be reachable in ET Connect
if (!Config.isCompass) {
  routes.push({
    path: "/newOffer",
    name: RouteNames.NEW_OFFER,
    component: CreateOffer,
  });
  routes.push({
    path: "/offerSent",
    name: RouteNames.OFFER_SENT,
    component: OfferSent,
  });
  routes.push({
    path: "/shipment-tracking",
    name: RouteNames.SHIPMENT_TRACKING,
    component: ShipmentTracking,
  });
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

const noLoginRequiredRoutes: Array<RouteNames> = [
  RouteNames.LOGIN,
  RouteNames.REGISTRATION,
  RouteNames.REGISTRATION_EMAIL_CONFIRM,
  RouteNames.EMAIL_CONFIRM_UPDATE,
  RouteNames.REGISTRATION_ACTIVATION,
  RouteNames.REGISTRATION_SUCCESS,
  RouteNames.TERMS_OF_USE,
  RouteNames.PRIVACY_POLICY,
  RouteNames.PASSWORD_FORGOT,
  RouteNames.PASSWORD_FORGOT_SUCCESS,
  RouteNames.PASSWORD_RESET,
  RouteNames.PASSWORD_CHANGE_REQUIRED,
  RouteNames.TERMS_OF_USE_ACCEPT,
  RouteNames.PAGE_NOT_FOUND,
  RouteNames.PRIVACY_POLICY_ACCEPT,
  RouteNames.DOWNLOAD,
  RouteNames.QUOTE_TOOL_PRINT,
  RouteNames.SHIPMENT_TRACKING,
];

const ET_CORE_REAUTHENTICATE_QUERY_VALUE = "etCore";

router.beforeEach(async (to, from, next) => {
  if (to.fullPath !== from.fullPath) {
    const canLeave = await confirmRegisteredLeaveGuards();

    if (!canLeave) {
      next(false);
      return;
    }
  }

  if (to.name === RouteNames.LOGIN) {
    const etcSessionAvailable = await hasValidEtcSession();
    const isEtCoreReauthentication =
      to.query.reauthenticate === ET_CORE_REAUTHENTICATE_QUERY_VALUE;

    // A valid persisted ETC session normally skips the login page. ET Core routes
    // may explicitly request an interactive login to obtain an ET Core token.
    if (etcSessionAvailable && !isEtCoreReauthentication) {
      const userHomeRouteName =
        useAuthStore().userHomeRouteName ?? RouteNames.DASHBOARD;

      const routeParameters =
        userHomeRouteName === RouteNames.USER_PROFILE
          ? { id: useAuthStore().currentUser?.id }
          : {};

      next({ name: userHomeRouteName, params: routeParameters });
    } else {
      next();
    }

    return;
  }

  // Other public routes do not need authentication state.
  if (noLoginRequiredRoutes.includes(to.name as RouteNames)) {
    next();
    return;
  }

  if (!(await hasValidEtcSession())) {
    next({ name: RouteNames.LOGIN, query: { redirect: to.fullPath } });
    return;
  }

  if (to.meta.requiresEtCoreAuth === true && !(await canOpenEtCoreRoute())) {
    next({
      name: RouteNames.LOGIN,
      query: {
        redirect: to.fullPath,
        reauthenticate: ET_CORE_REAUTHENTICATE_QUERY_VALUE,
      },
    });
    return;
  }

  next();
});

async function hasValidEtcSession(): Promise<boolean> {
  return useAuthStore().ensureInitialized();
}

async function canOpenEtCoreRoute(): Promise<boolean> {
  const token = await useEtCoreAuthStore().getValidAccessToken();

  if (token !== undefined) {
    return true;
  }

  // Do not create a login loop when an interactive ETC login succeeded but
  // ET Core authentication was unavailable. The ET Core page may then fail
  // independently while the rest of Compass remains usable.
  return useAuthStore().hasAuthenticatedInteractively;
}

export default router;
